import React, { useContext } from 'react';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import {
  AwsBuildingCostSummary,
  AwsInstanceCostSummary,
  AwsOrganizationCostSummary,
} from '@totem/components/administration/amazonWebServices/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

const formatMonthLabel = (month: string) =>
  dayjs(`${month}-01`).format('MMM YYYY');

const formatCost = (value: string | undefined, currency: string) => {
  if (!value) {
    return '—';
  }

  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return '—';
  }

  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
};

const AwsCostByHierarchyTable = () => {
  const { hierarchyCostReport } = useContext(AmazonWebServicesContext);
  const { months, currency, organizations } = hierarchyCostReport;

  const monthColumns = <
    T extends { monthlyCosts: Record<string, string> },
  >(): ColumnProps<T>[] =>
    months.map((month) => ({
      title: formatMonthLabel(month),
      key: month,
      align: 'right' as const,
      width: 110,
      render: (_, record: T) => (
        <span>{formatCost(record.monthlyCosts[month], currency)}</span>
      ),
    }));

  const organizationColumns: ColumnProps<AwsOrganizationCostSummary>[] = [
    {
      title: 'Organization',
      key: 'organization',
      fixed: 'left',
      width: 220,
      render: (_, record) => <span>{record.organization.name}</span>,
    },
    ...monthColumns<AwsOrganizationCostSummary>(),
  ];

  const buildingColumns: ColumnProps<AwsBuildingCostSummary>[] = [
    {
      title: 'Building',
      key: 'building',
      fixed: 'left',
      width: 220,
      render: (_, record) => <span>{record.building.name}</span>,
    },
    ...monthColumns<AwsBuildingCostSummary>(),
  ];

  const instanceColumns: ColumnProps<AwsInstanceCostSummary>[] = [
    {
      title: 'Instance',
      key: 'instance',
      fixed: 'left',
      width: 220,
      render: (_, record) => <span>{record.instance.name}</span>,
    },
    ...monthColumns<AwsInstanceCostSummary>(),
  ];

  return (
    <IBTable
      rowKey={(record) => record.organization.id}
      columns={organizationColumns}
      dataSource={organizations}
      pagination={false}
      scroll={{ x: 'max-content' }}
      expandable={{
        expandedRowRender: (record) => (
          <IBTable
            rowKey={(building) => building.building.id}
            columns={buildingColumns}
            dataSource={record.buildings}
            pagination={false}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (building) => (
                <IBTable
                  rowKey={(instance) => instance.instance.id}
                  columns={instanceColumns}
                  dataSource={building.instances}
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                />
              ),
              rowExpandable: (building) => building.instances.length > 0,
            }}
          />
        ),
        rowExpandable: (record) => record.buildings.length > 0,
      }}
    />
  );
};

export default AwsCostByHierarchyTable;
